/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

export type ParsedRetryConfig = {
  retry: number | false;
  retryDelay: number | ((attemptIndex: number) => number) | undefined;
};

const FALLBACK_RETRY_CONFIG: ParsedRetryConfig = {
  retry: false,
  retryDelay: undefined,
};

export function parseRetryConfigString(configString: string | undefined): ParsedRetryConfig {
  if (!configString) {
    return FALLBACK_RETRY_CONFIG;
  }

  const parts = configString.split(':').map((part) => part.trim());

  if (parts.length !== 3) {
    // Invalid retry config format: "${configString}". Expected: "retries:timeout:exponentialBackoff"`,
    return FALLBACK_RETRY_CONFIG;
  }

  const [retriesStr, timeoutStr, exponentialBackoffStr] = parts;

  const retries = parseInt(retriesStr, 10);
  const timeout = parseInt(timeoutStr, 10);
  const useExponentialBackoff = exponentialBackoffStr === 'true';

  if (Number.isNaN(retries) || Number.isNaN(timeout)) {
    return FALLBACK_RETRY_CONFIG;
  }

  const cappedRetries = Math.min(retries, 10);
  const cappedTimeout = Math.min(timeout, 30000);
  const retry = cappedRetries > 0 ? cappedRetries : false;

  if (!retry) {
    return { retry, retryDelay: undefined };
  }

  if (useExponentialBackoff && cappedTimeout > 0) {
    return {
      retry,
      retryDelay: (attemptIndex: number) =>
        Math.min(cappedTimeout * Math.pow(2, attemptIndex), 30000),
    };
  }

  if (!useExponentialBackoff && cappedTimeout > 0) {
    return {
      retry,
      retryDelay: cappedTimeout,
    };
  }

  return { retry, retryDelay: undefined };
}
